import { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { useGetRetentionPoliciesQuery } from '../../../../store/account/account'
import { type Account, type DataRetentionWarningEmailConfig } from '../../../../models/Account'
import { MultiSelectDropdown, type MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { Button } from '../../../../components/Button/Button'
import { useZocksAuth } from '../../../../components/ScopedBlock/hooks'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { EnterpriseManagedBanner } from '../EnterpriseManagedBanner'
import { getDataRetentionWarningEmailAvailability } from './dataRetentionWarningAvailability'
import { WhoGetsNotifiedHeader } from './WhoGetsNotifiedHeader'

interface DataRetentionNotificationFormValues {
  enabled: boolean
  frequency: 'daily' | 'weekly'
  includeInfo: 'linkOnly' | 'linkAndTitle'
  notifyMeetingOwner: boolean
  teamMemberIds: string[]
}

const DEFAULT_CONFIG: DataRetentionWarningEmailConfig = {
  enabled: false,
  frequency: 'daily',
  includeInfo: 'linkOnly',
  notifications: { meetingOwner: true, teamMemberIds: [] },
}

const getDefaultFormValues = (config?: DataRetentionWarningEmailConfig): DataRetentionNotificationFormValues => {
  const resolved = config ?? DEFAULT_CONFIG
  return {
    enabled: resolved.enabled,
    frequency: resolved.frequency,
    includeInfo: resolved.includeInfo,
    notifyMeetingOwner: resolved.notifications.meetingOwner,
    teamMemberIds: resolved.notifications.teamMemberIds,
  }
}

const WEEKLY_NOT_AVAILABLE_TOOLTIP = 'Weekly is available when every data retention policy is 1 week or longer.'
const SHORT_POLICY_WARNING = 'Data under a retention policy shorter than 24 hours will be excluded from these emails.'
const DAILY_FREQUENCY_HINT = "Sent every day at 9:00 AM in the recipient's timezone"
const WEEKLY_FREQUENCY_HINT = "Sent every Monday at 9:00 AM in the recipient's timezone"
const POLICY_SHORTENING_WARNING = 'Shortening a policy deletes data already past the new limit, with no email notification.'

export interface DataRetentionNotificationProps {
  account: Account
}

export const DataRetentionNotification = ({ account }: DataRetentionNotificationProps) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] })
  const { data: retentionPoliciesData, isFetching: isRetentionPoliciesLoading } = useGetRetentionPoliciesQuery({ accountId })
  const [isOpened, setIsOpened] = useState<boolean>(false)
  const { hasScope } = useZocksAuth()
  const hasAccountUpdateScope = hasScope('**', 'account:update', ['account:admin'])

  const isDataRetentionWarningEmailEnterpriseManaged = useMemo(() => account?.enterpriseConfig.dataRetentionWarningEmail != null, [account])

  const { isWeeklyFrequencyAvailable, hasShortEligiblePolicy } = useMemo(
    () => getDataRetentionWarningEmailAvailability(retentionPoliciesData?.data),
    [retentionPoliciesData],
  )

  const teamMemberOptions: MultiSelectOption[] = useMemo(() => {
    if (!usersData?.data) {
      return []
    }
    return usersData.data
      .filter((u) => u.id !== currentUserId)
      .sort((u1, u2) => getUserDisplayName(u1).localeCompare(getUserDisplayName(u2)))
      .map((u) => ({ id: u.id, label: getUserDisplayName(u) }))
  }, [usersData, currentUserId])

  const { control, handleSubmit, reset, formState } = useForm<DataRetentionNotificationFormValues>({
    defaultValues: getDefaultFormValues(account.effectiveConfig.dataRetentionWarningEmail),
  })

  const enabled = useWatch({ control, name: 'enabled' })

  const onSubmit = async (data: DataRetentionNotificationFormValues) => {
    const dataRetentionWarningEmail: DataRetentionWarningEmailConfig = {
      enabled: data.enabled,
      frequency: isWeeklyFrequencyAvailable || isRetentionPoliciesLoading ? data.frequency : 'daily',
      includeInfo: data.includeInfo,
      notifications: {
        meetingOwner: data.notifyMeetingOwner,
        teamMemberIds: data.teamMemberIds,
      },
    }

    const result = await updateAccount({
      accountId,
      payload: {
        config: {
          ...account.config,
          dataRetentionWarningEmail,
        },
      },
    })
    if ('data' in result && result.data) {
      reset(getDefaultFormValues(result.data.data.effectiveConfig.dataRetentionWarningEmail))
    }
  }

  const handleEnabledChange = (checked: boolean) => {
    handleSubmit((data) => onSubmit({ ...data, enabled: checked }))()
  }

  const handleCancel = () => {
    reset(getDefaultFormValues(account.effectiveConfig.dataRetentionWarningEmail))
  }

  return (
    <div className='d-flex flex-column gap-4 border-bottom zocks-pb-2 zocks-border-color-gray-400 border-1'>
      {isDataRetentionWarningEmailEnterpriseManaged ? (
        <EnterpriseManagedBanner message='Data Retention Notification setting is managed at the enterprise level and cannot be modified here.' />
      ) : null}
      <div className='d-flex zocks-gap-0.75 align-items-center justify-content-between'>
        <div className='d-flex flex-column'>
          <h6>Data Retention Notification</h6>
          <div className='paragraph-small zocks-text-gray-700'>
            Sent when a data retention policy is about to delete your data. Manage it in{' '}
            <NavLink to='/workspace/setup/settings/data-retention' className='zocks-text-gray-900 text-decoration-underline'>
              Data Retention Policies
            </NavLink>
            .
          </div>
          <div className='paragraph-small zocks-text-gray-700'>Only meetings and transcripts are included in these emails.</div>
        </div>
        <div className='d-flex flex-row gap-2 align-items-center'>
          <Controller
            name='enabled'
            control={control}
            render={({ field }) => (
              <Form.Switch
                id='data-retention-warning-email-enabled'
                checked={field.value}
                onChange={(e) => {
                  field.onChange(e.target.checked)
                  handleEnabledChange(e.target.checked)
                }}
                disabled={isRetentionPoliciesLoading || accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
              />
            )}
          />
          <Button onClick={() => setIsOpened((prev) => !prev)} disabled={!enabled}>
            <i className={isOpened ? 'bi bi-chevron-up' : 'bi bi-chevron-down'}></i>
          </Button>
        </div>
      </div>
      {enabled && isOpened ? (
        <>
          <div
            className={classNames(
              'd-flex align-items-center zocks-gap-0.75 px-3 zocks-py-0.75 w-100 zocks-border-0.25',
              hasShortEligiblePolicy ? 'zocks-bg-color-warning' : 'zocks-bg-color-primary-light',
            )}
          >
            <i className={hasShortEligiblePolicy ? 'bi bi-exclamation-triangle' : 'bi bi-info-square zocks-text-primary'} />
            <div className={classNames('paragraph-small d-flex flex-column', { 'zocks-text-gray-700': !hasShortEligiblePolicy })}>
              {hasShortEligiblePolicy ? <span>{SHORT_POLICY_WARNING}</span> : null}
              <span>{POLICY_SHORTENING_WARNING}</span>
            </div>
          </div>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <div className='paragraph-medium medium'>Frequency</div>
            <Controller
              name='frequency'
              control={control}
              render={({ field }) => (
                <div className='d-flex flex-column align-items-start gap-2'>
                  <Form.Check
                    type='radio'
                    id='frequency-daily-data-retention'
                    label={
                      <span className='d-flex align-items-center gap-2'>
                        <span>Daily</span>
                        <span className='paragraph-small zocks-text-gray-700'>- {DAILY_FREQUENCY_HINT}</span>
                      </span>
                    }
                    value='daily'
                    checked={field.value === 'daily'}
                    onChange={() => field.onChange('daily')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
                  />
                  <Tooltip title={!isRetentionPoliciesLoading && !isWeeklyFrequencyAvailable ? WEEKLY_NOT_AVAILABLE_TOOLTIP : undefined} placement='top'>
                    <span className='d-inline-block'>
                      <Form.Check
                        type='radio'
                        id='frequency-weekly-data-retention'
                        label={
                          <span className='d-flex align-items-center gap-2'>
                            <span>Weekly</span>
                            <span className='paragraph-small zocks-text-gray-700'>- {WEEKLY_FREQUENCY_HINT}</span>
                          </span>
                        }
                        value='weekly'
                        checked={field.value === 'weekly'}
                        onChange={() => field.onChange('weekly')}
                        className={classNames({ 'pe-none': !isWeeklyFrequencyAvailable })}
                        disabled={accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged || !isWeeklyFrequencyAvailable}
                      />
                    </span>
                  </Tooltip>
                </div>
              )}
            />
          </div>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <div className='paragraph-medium medium'>Email content</div>
            <Controller
              name='includeInfo'
              control={control}
              render={({ field }) => (
                <div className='d-flex flex-column gap-2'>
                  <Form.Check
                    type='radio'
                    id='include-link-only-data-retention'
                    label='Link only'
                    value='linkOnly'
                    checked={field.value === 'linkOnly'}
                    onChange={() => field.onChange('linkOnly')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
                  />
                  <Form.Check
                    type='radio'
                    id='include-link-and-title-data-retention'
                    label='Link and meeting title'
                    value='linkAndTitle'
                    checked={field.value === 'linkAndTitle'}
                    onChange={() => field.onChange('linkAndTitle')}
                    disabled={accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
                  />
                </div>
              )}
            />
          </div>
          <div className='d-flex flex-column zocks-gap-0.75'>
            <WhoGetsNotifiedHeader
              items={[
                'Private meetings: only the meeting owner gets the deletion warning.',
                'Shared meetings: only the team members the meeting is shared with get the deletion warning.',
              ]}
            />
            <Controller
              name='notifyMeetingOwner'
              control={control}
              render={({ field }) => (
                <Form.Check
                  type='checkbox'
                  id='notify-meeting-owner-data-retention'
                  label='Meeting owner'
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  disabled={accountUpdateLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
                />
              )}
            />
            <div>Select team members who will receive the email notification in addition to the meeting owner</div>
            <div className='d-flex flex-column gap-2'>
              <Controller
                name='teamMemberIds'
                control={control}
                render={({ field }) => (
                  <MultiSelectDropdown
                    name={field.name}
                    options={teamMemberOptions}
                    initialSelectedOptions={field.value}
                    onSelectedChanged={field.onChange}
                    placeholder='Select team members…'
                    disabled={accountUpdateLoading || isUsersLoading || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
                  />
                )}
              />
            </div>
          </div>
          <div className='d-flex zocks-gap-0.625'>
            <Button
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              loading={accountUpdateLoading}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
            >
              Save changes
            </Button>
            <Button
              variant='borderless'
              className={classNames({ 'zocks-text-primary': !accountUpdateLoading && formState.isDirty })}
              onClick={handleCancel}
              disabled={accountUpdateLoading || !formState.isDirty || !hasAccountUpdateScope || isDataRetentionWarningEmailEnterpriseManaged}
            >
              Cancel
            </Button>
          </div>
        </>
      ) : null}
    </div>
  )
}
